<template>
  <div>
    <div>
      <div>
        <article class="box">
          <p class="title">Annotations</p>
          <div v-if="annotationCategories.length"
            class="columns">
            <div v-for="(column, columnIndex) in annotationColumns"
              :key="columnIndex"
              class="column">
              <div v-for="row in column"
                :key="row.remarkTypeId"
                class="field">
                <label class="label">{{ row.label }}</label>
                <div class="control"
                  :class="{ 'is-loading': savingAnnotations[row.remarkTypeId] }">
                  <textarea class="textarea"
                    v-model="row.annotation.remarks"
                    :rows="rows"
                    :disabled="!!savingAnnotations[row.remarkTypeId]"
                    @focus="captureAnnotationSnapshot(row.remarkTypeId)"
                    @blur="handleAnnotationBlur(row.remarkTypeId)" />
                </div>
              </div>
            </div>
          </div>
          <div v-else
            class="content">
            <p>No item categories are enabled for annotations.</p>
          </div>
        </article>
      </div>
    </div>
  </div>
</template>

<script>
import { RemarkTypes, EventHubTypes } from '@/enums'
import Guid from '@/components/Guid'
import RemarkService from '@/services/RemarkService'

export default {
  name: 'QuoteAnnotations',
  props: {
    value: null,
    autoSaveEnabled: { type: Boolean, default: false }
  },
  data() {
    return {
      innerValue: null,
      rows: 2,
      savingAnnotations: {},
      annotationSnapshots: {},
      draftAnnotations: {}
    }
  },
  computed: {
    // Categories come from /companies/itemtypes (loaded into $company.itemCategories
    // on app start), so enabling/disabling a category adds/removes its annotation.
    // A category is only shown if it maps to a remark type, as that is what gets saved.
    annotationCategories() {
      const itemCategories = this.$company?.itemCategories || []
      return itemCategories
        .filter((c) => c.enabled && RemarkTypes[c.itemCategoryType] !== undefined)
        .map((c) => ({
          remarkTypeId: RemarkTypes[c.itemCategoryType],
          label: c.displayName || c.itemCategoryType
        }))
    },
    displayedRemarkTypes() {
      return this.annotationCategories.map((c) => c.remarkTypeId)
    },
    annotationColumns() {
      const rows = this.annotationCategories
        .map((c) => ({ ...c, annotation: this.getAnnotation(c.remarkTypeId) }))
        .filter((r) => r.annotation)
      const half = Math.ceil(rows.length / 2)
      return [rows.slice(0, half), rows.slice(half)]
    }
  },
  watch: {
    displayedRemarkTypes() {
      this.initAnnotations()
    }
  },
  created() {
    this.innerValue = this.value
    this.initAnnotations()
  },
  beforeDestroy() {
    this.$eventHub.$off(EventHubTypes.EntitySaved)
  },
  mounted() {
    this.$eventHub.$on(EventHubTypes.EntitySaved, () => {
      this.innerValue = this.value
      this.initAnnotations()
    })
  },
  methods: {
    initAnnotations() {
      // Drafts for missing remark types; promoted to the entity only on edit
      // (see handleAnnotationBlur), so empty rows aren't flagged as unsaved.
      this.draftAnnotations = {}
      for (const remarkType of this.displayedRemarkTypes) {
        const existing = this.innerValue.annotations.find(r => r.remarkTypeId === remarkType)
        if (!existing) {
          this.$set(this.draftAnnotations, remarkType, {
            isAlert: false,
            isDeleted: false,
            isNew: true,
            remarkDateTime: new Date(),
            remarkId: Guid.newGuid(),
            remarkTypeId: remarkType,
            assetId: this.innerValue.quoteId,
            remarks: ''
          })
        }
      }
    },
    getAnnotation(remarkType) {
      const existing = this.innerValue.annotations.find(r => r.remarkTypeId === remarkType)
      if (existing) return existing
      return this.draftAnnotations[remarkType]
    },
    captureAnnotationSnapshot(remarkType) {
      this.$set(this.annotationSnapshots, remarkType, this.getAnnotation(remarkType).remarks)
    },
    async handleAnnotationBlur(remarkType) {
      const annotation = this.getAnnotation(remarkType)
      if (annotation.remarks === this.annotationSnapshots[remarkType]) return
      // Promote the draft to the entity so both manual save and autosave see it.
      // Must run before the autosave gate below.
      if (annotation.isNew && !this.innerValue.annotations.includes(annotation)) {
        this.innerValue.annotations.push(annotation)
      }
      if (!this.autoSaveEnabled) return
      this.$set(this.savingAnnotations, remarkType, true)
      this.$eventHub.$emit(EventHubTypes.AutoSaveAnnotationsStatus, { isSaving: true })
      try {
        if (annotation.isNew) {
          await RemarkService.postEntity(annotation)
          annotation.isNew = false
        } else {
          await RemarkService.putEntity(annotation)
        }
        this.$eventHub.$emit(EventHubTypes.AutoSaveAnnotationsStatus, { isSaving: false, isFailed: false })
      } catch (e) {
        this.$eventHub.$emit(EventHubTypes.AutoSaveAnnotationsStatus, {
          isSaving: false,
          isFailed: true,
          failMessage: e?.message || 'Save failed'
        })
      } finally {
        this.$set(this.savingAnnotations, remarkType, false)
      }
    }
  }
}
</script>
